import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import {
  ACTION,
  ERROR,
  FORM_EVENT,
  FORM_MODE,
  STATUS,
} from "../shared/constant";

const REVIEW_STATUS = require("../../../../server/shared/constants/status.constant");
const ROLES = require("../../../../server/shared/constants/role.constant");
const CONTACTS = require("../../../../server/shared/constants/contact.constant");
const DISPLAY = require("../../../../server/shared/constants/display.constant");

@Component({
  selector: "participation-form",
  templateUrl: "./participation-form.component.html",
  styleUrls: ["./participation-form.component.scss"],
})
export class ParticipationFormComponent implements OnInit {
  formVerCode: string;
  formSubCode: string;
  mode: string = FORM_MODE.EDIT;
  env: any = environment;
  currentUser: any;
  approvers: any;
  showEmailContent: boolean = false;
  showEmailSupplierToolbar: boolean = false;
  latestSentEmailSupplierInfo: any;
  showRestrictedToolbar: boolean = false;
  showRestrictedSiteAccessContent: boolean = false;
  restrictedSiteAccessInfo: any;
  showIndividualRestrictedSiteAccessContent: boolean = false;
  individualRestrictedSiteAccessInfo: any;
  participationForm$: Observable<any>;
  assignedProgramName: any = undefined;
  assignedProgramNumbersForDomain: any = undefined;
  programNumber: any = undefined;
  programLocation: any = undefined;
  formStatus: string = FORM_EVENT.LOADING;
  participationFormCode: string;
  participationForm: any;
  enableSave: boolean = true;
  amendWithWF: boolean = false;
  amendmentHistory: any;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<any>
  ) {
    this.participationForm$ = this.store.select("PARTICIPATION_FORM_REDUCER");
  }

  ngOnInit() {
    // FQ Matrix : Modes and submission codes
    // +----------------+--------------------------------------------------------------+
    // |       ><       | Init Form | Init Draft | Sub Form | Amend Form | Amend Draft |
    // +----------------+-----------+------------+----------+------------+-------------|
    // | submissionCode | null      | subCode1   | subCode1 | subCode1  => subCode2    |
    // |----------------+-----------+------------+----------+------------+-------------|
    // | mode           | edit      | edit       | readOnly | amend     => edit        |
    // +-------------------------------------------------------------------------------+

    this.currentUser = this.route.snapshot.data["currentUser"];
    this.participationFormCode =
      this.route.snapshot.data["participationFormCode"];
    this.participationForm = this.route.snapshot.data["participationForm"];
    this.formSubCode = this.participationForm
      ? this.participationForm.formSubmissionCode
      : null;

    this.route.params.forEach((params: Params) => {
      let routeSubmissionCode = params["submissionCode"];

      if (routeSubmissionCode && routeSubmissionCode !== this.formSubCode) {
        // change the sub code in the url
        this.router.navigate([
          "participationForm/submissionCode/" + this.formSubCode,
        ]);
      }
    });

    this.mode = this.formSubCode ? FORM_MODE.READONLY : FORM_MODE.EDIT;

    if (this.participationForm.workFlows) {
      this.amendmentHistory = this.participationForm.workFlows.filter(
        (workFlow) => {
          if (!workFlow.changes || workFlow.changes.length === 0) {
            return false;
          }

          if (workFlow.actionBy) {
            if (workFlow.actionBy.role) {
              workFlow.roleValue = DISPLAY[workFlow.actionBy.role];
            } else {
              workFlow.roleValue = null;
            }
          }

          return (
            [
              REVIEW_STATUS.AMEND_WITH_WORKFLOW.name,
              REVIEW_STATUS.CLEANUP.name,
            ].indexOf(workFlow.action) > -1
          );
        }
      );
    }

    if (this.isSubmitted() && this.isReadOnly()) {
      this.buildApprovers(this.participationForm);

      this.initEmailSupplierToolbar();

      if (this.participationForm.program) {
        this.assignedProgramName = this.participationForm.program.name;
      }
    }
  }

  initEmailSupplierToolbar() {
    this.showEmailSupplierToolbar =
      this.participationForm.workFlows.some((workFlow) => {
        return workFlow.action === REVIEW_STATUS.PROGRAM_NAME_GENERATED.name;
      }) && this.isUcopAdmin();

    this.showEmailContent =
      (this.participationForm.status ===
        REVIEW_STATUS.PENDING_VENDOR_ACCOUNT_CREATION.name ||
        this.participationForm.status ===
          REVIEW_STATUS.PROGRAM_NAME_GENERATED.name ||
        this.participationForm.status ===
          REVIEW_STATUS.VENDOR_ACCOUNT_CREATED.name) &&
      this.isUcopAdmin();

    if (this.showEmailSupplierToolbar) {
      this.latestSentEmailSupplierInfo = this.getLatestSentEmailSupplierInfo(
        this.participationForm
      );
    }

    this.showRestrictedToolbar =
      this.participationForm.workFlows.some((workFlow) => {
        return workFlow.action === REVIEW_STATUS.PROGRAM_NAME_GENERATED.name;
      }) && this.isUcopAdmin();

    this.showRestrictedSiteAccessContent =
      this.showRestrictedToolbar && this.participationForm.restrictedSiteAccess;
    this.showIndividualRestrictedSiteAccessContent =
      this.showRestrictedToolbar &&
      this.participationForm.individualRestrictedSiteAccess;

    if (this.showRestrictedToolbar) {
      this.restrictedSiteAccessInfo = this.getRestrictedSiteAccessApprovalInfo(
        this.participationForm,
        "GRANTED_ACCESS_TO_RESTRICTED_SITE"
      );
      this.individualRestrictedSiteAccessInfo =
        this.getRestrictedSiteAccessApprovalInfo(
          this.participationForm,
          "GRANTED_ACCESS_TO_INDIVIDUAL_RESTRICTED_SITE"
        );
    }
  }

  statusChange(formEvent: any) {
    if (!formEvent) this.router.navigate([ERROR.UNKNOWN.url]);

    this.formStatus = formEvent.status;

    switch (formEvent.status) {
      case FORM_EVENT.SUBMITTED:
      case FORM_EVENT.SAVED: {
        if (!formEvent.submissionCode)
          this.router.navigate([ERROR.UNKNOWN.url]);

        if (this.mode === FORM_MODE.AMEND) {
          this.handleAmendment(formEvent);
          break;
        }

        // regular submissions
        this.handleSubmission(formEvent);
        break;
      }
      case FORM_EVENT.ERROR: {
        this.router.navigate([ERROR.UNKNOWN.url]);
        break;
      }
    }
  }

  handleSubmission(formEvent) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_SAVE,
      payload: { formSubmissionCode: formEvent.submissionCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((s) => {
        this.formSubCode = formEvent.submissionCode;
        this.participationForm = s.data;
        if (s.data.status === REVIEW_STATUS.SUBMITTED.name)
          this.router.navigate(["confirmation"], {
            queryParams: { mode: "submit" },
          });
      });
  }

  handleAmendment(formEvent) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_AMEND,
      payload: {
        previousSubmissionCode: this.formSubCode,
        nextSubmissionCode: formEvent.submissionCode,
        amendWithWF: this.amendWithWF,
      },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((s) => {
        this.formSubCode = formEvent.submissionCode;
        this.participationForm = s.data;
        this.router.navigate(["confirmation"], {
          queryParams: { mode: this.amendWithWF ? "amend" : "submit" },
        });
      });
  }

  discardDraft() {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_INACTIVATE,
      payload: { formSubmissionCode: this.formSubCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe(() => {
        window.location.reload();
      });
  }

  continueDraft() {
    this.mode = FORM_MODE.EDIT;
  }

  amend(amendWithWF) {
    this.mode = FORM_MODE.AMEND;
    this.enableSave = false;
    this.amendWithWF = amendWithWF;
  }

  emailSupplier() {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_EMAIL_SUPPLIER,
      payload: { formSubmissionCode: this.formSubCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        this.latestSentEmailSupplierInfo = this.getLatestSentEmailSupplierInfo(
          state.data
        );
      });
  }

  restrictedSiteAccessApproval() {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_RESTRICTED_SITE_APPROVAL,
      payload: { formSubmissionCode: this.formSubCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        this.showRestrictedToolbar =
          this.participationForm.workFlows.some((workFlow) => {
            return (
              workFlow.action === REVIEW_STATUS.PROGRAM_NAME_GENERATED.name
            );
          }) && this.isUcopAdmin();
        this.showRestrictedSiteAccessContent =
          this.showRestrictedToolbar &&
          this.participationForm.restrictedSiteAccess;

        if (this.showRestrictedToolbar) {
          this.restrictedSiteAccessInfo =
            this.getRestrictedSiteAccessApprovalInfo(
              this.participationForm,
              "GRANTED_ACCESS_TO_RESTRICTED_SITE"
            );
        }
      });
  }

  individualRestrictedSiteAccessApproval() {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_INDIVIDUAL_RESTRICTED_SITE_APPROVAL,
      payload: { formSubmissionCode: this.formSubCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        this.showRestrictedToolbar =
          this.participationForm.workFlows.some((workFlow) => {
            return (
              workFlow.action === REVIEW_STATUS.PROGRAM_NAME_GENERATED.name
            );
          }) && this.isUcopAdmin();
        this.showIndividualRestrictedSiteAccessContent =
          this.showRestrictedToolbar &&
          this.participationForm.individualRestrictedSiteAccess;

        if (this.showRestrictedToolbar) {
          this.individualRestrictedSiteAccessInfo =
            this.getRestrictedSiteAccessApprovalInfo(
              this.participationForm,
              "GRANTED_ACCESS_TO_INDIVIDUAL_RESTRICTED_SITE"
            );
        }
      });
  }

  approve(comments) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_APPROVE,
      payload: {
        formSubmissionCode: this.formSubCode,
        workFlows: [{ comment: comments }],
      },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        if (this.participationForm.program)
          this.assignedProgramName = this.participationForm.program.name;
        this.buildApprovers(state.data);
        this.initEmailSupplierToolbar();
      });
  }

  decline(comments) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_DECLINE,
      payload: {
        formSubmissionCode: this.formSubCode,
        workFlows: [{ comment: comments }],
      },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        this.buildApprovers(state.data);
      });
  }

  returnApplication(comments) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_RETURN,
      payload: { formSubmissionCode: this.formSubCode, comments: comments },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        this.buildApprovers(state.data);
      });
  }

  saveProgramName(program) {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_SAVE_PROGRAM,
      payload: {
        formSubmissionCode: this.formSubCode,
        programLocation: program.programLocation,
        programNumber: program.programNumber,
      },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        this.participationForm = state.data;
        if (this.participationForm.program) {
          this.assignedProgramName = this.participationForm.program.name;
        }
        this.showEmailSupplierToolbar =
          this.participationForm.workFlows.some((workFlow) => {
            return (
              workFlow.action === REVIEW_STATUS.PROGRAM_NAME_GENERATED.name
            );
          }) && this.isUcopAdmin();
        this.showEmailContent =
          (this.participationForm.status ===
            REVIEW_STATUS.PENDING_VENDOR_ACCOUNT_CREATION.name ||
            this.participationForm.status ===
              REVIEW_STATUS.PROGRAM_NAME_GENERATED.name ||
            this.participationForm.status ===
              REVIEW_STATUS.VENDOR_ACCOUNT_CREATED.name) &&
          this.isUcopAdmin();
        if (this.showEmailSupplierToolbar) {
          this.latestSentEmailSupplierInfo =
            this.getLatestSentEmailSupplierInfo(this.participationForm);
        }
      });
  }

  generateProgramName() {
    this.store.dispatch({
      type: ACTION.PARTICIPATION_FORM_GENERATE_PROGRAM,
      payload: { formSubmissionCode: this.formSubCode },
    });
    this.participationForm$
      .filter((state) => state.status === STATUS.COMPLETED)
      .first()
      .subscribe((state) => {
        if (state.data && state.data.program) {
          this.programNumber = state.data.program.number;
          this.programLocation = state.data.program.location;
          this.assignedProgramNumbersForDomain =
            state.data.program.assignedProgramNumbersForDomain;
        }
      });
  }

  buildApprovers(form) {
    this.approvers = [];

    let campusAdminApproved = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.APPROVED_BY_CAMPUS_ADMIN.name
    );
    let campusAdminDeclined = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.DECLINED_BY_CAMPUS_ADMIN.name
    );
    let campusAdminReturned = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.CORRECTION_REQ_BY_CAMPUS_ADMIN.name
    );

    let campusAdminComments = this.getActionComment(
      form.workFlows,
      ROLES.ROLE_CAMPUS_ADMIN
    );
    let campusAdminName = this.getAdminName(
      form.workFlows,
      ROLES.ROLE_CAMPUS_ADMIN
    );
    let campusAdminActionDate = this.getActionDate(
      form.workFlows,
      ROLES.ROLE_CAMPUS_ADMIN
    );

    let ucopAdminApproved = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.APPROVED_BY_UCOP_ADMIN.name
    );
    let ucopAdminDeclined = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.DECLINED_BY_UCOP_ADMIN.name
    );
    let ucopAdminReturned = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.CORRECTION_REQ_BY_UCOP_ADMIN.name
    );

    let ucopAdminComments = this.getActionComment(
      form.workFlows,
      ROLES.ROLE_UCOP_ADMIN
    );
    let ucopAdminName = this.getAdminName(
      form.workFlows,
      ROLES.ROLE_UCOP_ADMIN
    );
    let ucopAdminActionDate = this.getActionDate(
      form.workFlows,
      ROLES.ROLE_UCOP_ADMIN
    );

    let campusAdmin = {
      role: "EHS Liaison",
      name: campusAdminName,
      hasAccess: this.isCampusAdmin(),
      hasApproved: campusAdminApproved,
      hasDenied: campusAdminDeclined,
      hasReturned: campusAdminReturned,
      comments: campusAdminComments,
      actionDate: campusAdminActionDate,
    };

    let ucopAdmin = {
      role: "UCOP EHS",
      name: ucopAdminName,
      hasAccess: this.isUcopAdmin(),
      hasApproved: ucopAdminApproved,
      hasDenied: ucopAdminDeclined,
      hasReturned: ucopAdminReturned,
      comments: ucopAdminComments,
      actionDate: ucopAdminActionDate,
    };

    this.approvers.push(campusAdmin);
    this.approvers.push(ucopAdmin);
  }

  isActionDone(workflows, action) {
    return (
      workflows.filter((workFlow) => workFlow.action === action).length > 0
    );
  }

  getActionComment(workFlows, role) {
    let filteredWorkFlows = this.getWorkFlowsByRole(workFlows, role);

    if (filteredWorkFlows.length > 0) return filteredWorkFlows[0].comment;
    return "";
  }

  getAdminName(workFlows, role) {
    let filteredWorkFlows = this.getWorkFlowsByRole(workFlows, role);

    if (filteredWorkFlows.length > 0) {
      return (
        filteredWorkFlows[0].actionBy.firstName +
        " " +
        filteredWorkFlows[0].actionBy.lastName
      );
    }

    return "";
  }

  getWorkFlowsByRole(workFlows, role) {
    let filteredWorkFlows = [];
    if (role === ROLES.ROLE_CAMPUS_ADMIN) {
      filteredWorkFlows = workFlows.filter(
        (workFlow) =>
          workFlow.action === REVIEW_STATUS.APPROVED_BY_CAMPUS_ADMIN.name ||
          workFlow.action === REVIEW_STATUS.DECLINED_BY_CAMPUS_ADMIN.name ||
          workFlow.action === REVIEW_STATUS.CORRECTION_REQ_BY_CAMPUS_ADMIN.name
      );
    } else if (role === ROLES.ROLE_UCOP_ADMIN) {
      filteredWorkFlows = workFlows.filter(
        (workFlow) =>
          workFlow.action === REVIEW_STATUS.APPROVED_BY_UCOP_ADMIN.name ||
          workFlow.action === REVIEW_STATUS.DECLINED_BY_UCOP_ADMIN.name ||
          workFlow.action === REVIEW_STATUS.CORRECTION_REQ_BY_UCOP_ADMIN.name
      );
    }
    return filteredWorkFlows;
  }

  getActionDate(workFlows, role) {
    let filteredWorkFlows = this.getWorkFlowsByRole(workFlows, role);

    if (filteredWorkFlows.length > 0) return filteredWorkFlows[0].actionDate;
    return "";
  }

  isUcopAdminAndApproved(form) {
    let isUCOPApproved = this.isActionDone(
      form.workFlows,
      REVIEW_STATUS.APPROVED_BY_UCOP_ADMIN.name
    );
    return isUCOPApproved && this.isUcopAdmin();
  }

  getLatestSentEmailSupplierInfo(form) {
    let emailSupplier = form.workFlows.filter(
      (workflow) =>
        workflow.action === REVIEW_STATUS.PENDING_VENDOR_ACCOUNT_CREATION.name
    );
    return emailSupplier.length > 0
      ? emailSupplier.reduce((prev, current) =>
          prev.actionDate > current.actionDate ? prev : current
        )
      : null;
  }

  getRestrictedSiteAccessApprovalInfo(form, action) {
    let workFlows = form.workFlows.filter(
      (workflow) => workflow.action === action
    );
    return workFlows.length > 0
      ? workFlows.reduce((prev, current) =>
          prev.actionDate > current.actionDate ? prev : current
        )
      : null;
  }

  isReadOnly() {
    return this.mode === FORM_MODE.READONLY;
  }

  isSubmitted() {
    return (
      this.participationForm &&
      this.participationForm.status !== REVIEW_STATUS.DRAFT.name
    );
  }

  isEdit() {
    return this.mode === FORM_MODE.EDIT;
  }

  isDraft() {
    return (
      this.participationForm &&
      this.participationForm.status === REVIEW_STATUS.DRAFT.name
    );
  }

  isUnitRep() {
    return this.currentUser.userId === this.participationForm.createdBy.userId;
  }

  isCampusAdmin() {
    return this.currentUser.role === ROLES.ROLE_CAMPUS_ADMIN;
  }

  isUcopAdmin() {
    return this.currentUser.role === ROLES.ROLE_UCOP_ADMIN;
  }

  canRoleAmendWithWF() {
    const allowedContactTypes = [
      CONTACTS.PRIMARY_CONTACT,
      CONTACTS.ALTERNATE_CONTACT,
      CONTACTS.PRIMARY_SUPERVISOR,
      CONTACTS.ALTERNATE_SUPERVISOR,
      CONTACTS.PRIMARY_PURCHASER,
      CONTACTS.ALTERNATE_PURCHASER,
    ];

    if (this.currentUser.campusCode !== this.participationForm.campusCode)
      return false;

    if (this.isUnitRep() || this.isCampusAdmin() || this.isUcopAdmin())
      return true;

    let currentUserContact = this.participationForm.contacts.find(
      (contact) => contact.userId === this.currentUser.userId
    );
    return (
      currentUserContact &&
      !!allowedContactTypes.find(
        (contactType) => currentUserContact.contactType === contactType
      )
    );
  }

  canStatusAmendWithWF() {
    const allowedStatuses = [
      REVIEW_STATUS.APPROVED_BY_UCOP_ADMIN.name,
      REVIEW_STATUS.PROGRAM_NAME_GENERATED.name,
      REVIEW_STATUS.PENDING_VENDOR_ACCOUNT_CREATION.name,
      REVIEW_STATUS.VENDOR_ACCOUNT_CREATED.name,
    ];

    return (
      this.isReadOnly() &&
      !!allowedStatuses.find(
        (status) => this.participationForm.status === status
      )
    );
  }
}



// WEBPACK FOOTER //
// /home/runner/work/slipresistant/slipresistant/client/src/app/participation-form/participation-form.component.ts